import { z } from 'zod';
import { isoDateStringSchema, objectIdSchema } from './common.js';

/**
 * The stage ladders a shop sells down — its team pipelines, and what the stages of each mean.
 *
 * THIS FILE DEFINES SHAPES, NOT RECORDS. A pipeline here is a definition: a name and an ordered
 * list of stages. What travels down it is a DEAL (`schemas/deals.ts`), and the split is the whole
 * design — a shop that sells two different ways ("stitching" and "alterations" are not the same
 * conversation) needs two ladders, and a ladder that lived on the deal would be re-typed on every
 * record and never agree with itself twice.
 *
 * This file owns where a piece of WORK stands, which is a different question with a different
 * answer per shop, asked only once a deal exists. One customer can hold three deals in three
 * pipelines.
 *
 * The model is Zoho Bigin's, deliberately, because a shop moving off Bigin should recognise it:
 * their Deals module is literally called `Pipelines`, every deal belongs to one team pipeline, and
 * each stage of that pipeline carries a probability and a FORECAST TYPE saying whether it is still
 * live or an ending. That last field is the one doing real work — see `DEAL_FORECAST_TYPES`.
 */

export const PIPELINE_NAME_MAX_LENGTH = 60;
export const PIPELINE_STAGE_LABEL_MAX_LENGTH = 40;

/** A cap, not a target. Past this the board is a horizontal scroll nobody reads to the end of. */
export const MAX_PIPELINE_STAGES = 15;
/** Likewise: a shop with more ways of selling than this has a naming problem, not a CRM problem. */
export const MAX_PIPELINES = 10;

/**
 * What a stage MEANS, as opposed to what it is called.
 *
 * Three, and the count is load-bearing. A shop can rename every stage and reorder them freely, so
 * nothing may ever read a stage's LABEL to decide whether a deal is still live — "Closed with
 * Sale", "Delivered" and "Booked" are all the same answer, and a board that pattern-matched on the
 * word "won" would get every one of them wrong. This is the field that answers it instead: the
 * board greys the endings, the deal section stops nagging, and any figure worth reporting
 * (`won value`, `open value`) is a sum grouped by this and nothing else.
 *
 * Bigin spells these `Open` / `Closed Won` / `Closed Lost`; the meaning is identical and the
 * shorter spelling is what the rest of this codebase already uses for endings.
 */
export const DEAL_FORECAST_TYPES = ['open', 'won', 'lost'] as const;
export type DealForecastType = (typeof DEAL_FORECAST_TYPES)[number];
export const dealForecastTypeSchema = z.enum(DEAL_FORECAST_TYPES);

/** The endings. A deal in one of these is out of the working board. */
export const CLOSED_FORECAST_TYPES = ['won', 'lost'] as const;

export function isOpenForecastType(type: DealForecastType): boolean {
  return type === 'open';
}

export const pipelineStageKeySchema = z
  .string()
  .trim()
  .min(1)
  .max(40)
  .regex(/^[a-z][a-z0-9_]*$/, 'A stage key is lower snake_case');

export const pipelineStageLabelSchema = z
  .string()
  .trim()
  .min(1, 'Give the stage a name')
  .max(PIPELINE_STAGE_LABEL_MAX_LENGTH)
  .transform((value) => value.replace(/\s+/g, ' '));

export const pipelineNameSchema = z
  .string()
  .trim()
  .min(1, 'Give the pipeline a name')
  .max(PIPELINE_NAME_MAX_LENGTH)
  .transform((value) => value.replace(/\s+/g, ' '));

/**
 * One rung of the ladder.
 *
 * The KEY is derived once from the label the shop first typed and is then immutable — the same
 * rule craft roles and custom fields follow, for the same reason: a stage renamed after a year must
 * not orphan the deals sitting in it. Renaming writes `label`; the key never moves.
 *
 * `probability` is Bigin's, and it is advisory. It weights a forecast and it tells somebody
 * reading the board how seriously to take a column; nothing branches on it. A won stage is won
 * because `forecastType` says so, never because the number reached 100.
 */
export const pipelineStageSchema = z.object({
  key: pipelineStageKeySchema,
  label: z.string(),
  forecastType: dealForecastTypeSchema,
  /** 0–100. Advisory — see above. */
  probability: z.number().int().min(0).max(100),
  /** Position on the board, left to right. Dense and renumbered on every write. */
  order: z.number().int(),
});
export type PipelineStage = z.infer<typeof pipelineStageSchema>;

/**
 * One team pipeline as every screen reads it.
 *
 * `name` is a plain `z.string()` here and NOT `pipelineNameSchema`, which trims and collapses
 * whitespace. Every response schema in this codebase is written that way: the API client types one
 * as `ZodType<TResult>`, whose input and output must be the same type, and a `.transform()` makes
 * them differ. Normalising belongs on the way IN — see `createPipelineSchema` below.
 */
export const pipelineSchema = z.object({
  id: objectIdSchema,
  name: z.string(),
  /** Ordered left to right. Never empty — see `pipelineStagesInputSchema`. */
  stages: z.array(pipelineStageSchema),
  /**
   * The one a new deal lands in when nobody picked. Exactly one pipeline carries it, enforced by
   * the service rather than hoped for: without it, "New deal" on a customer would have to ask a
   * question before it could ask the useful ones.
   */
  isDefault: z.boolean(),
  /**
   * Retired pipelines leave every picker but still resolve stage labels for the deals already in
   * them. Deleting is reserved for a pipeline nothing has ever used; see `deletePipeline`.
   */
  active: z.boolean(),
  createdAt: isoDateStringSchema,
  updatedAt: isoDateStringSchema,
});
export type Pipeline = z.infer<typeof pipelineSchema>;

export const pipelineListSchema = z.object({
  items: z.array(pipelineSchema),
});
export type PipelineList = z.infer<typeof pipelineListSchema>;

/**
 * A stage as the settings screen sends it.
 *
 * `key` is optional and is how an EXISTING stage is recognised across an edit: present means
 * "this is that stage, renamed and/or moved", absent means "this one is new". The array's order
 * IS the board's order, so there is no separate reorder endpoint to fall out of step with this
 * one — a shop drags a row and sends the list.
 */
export const pipelineStageInputSchema = z.object({
  key: pipelineStageKeySchema.optional(),
  label: pipelineStageLabelSchema,
  forecastType: dealForecastTypeSchema,
  probability: z.number().int().min(0).max(100).default(0),
});
export type PipelineStageInput = z.input<typeof pipelineStageInputSchema>;

/**
 * The stage list, with the one rule that cannot be relaxed: SOMETHING must still be open.
 *
 * A pipeline made entirely of endings is a board with no column a deal can be created into, and
 * the failure is silent — the shop gets an empty picker and no explanation. Refusing it here means
 * the settings form and the endpoint give the same answer.
 *
 * Notably NOT required: a won stage, or a lost one. Bigin ships pipelines missing each, and a shop
 * whose "Alterations" ladder ends at "Collected" is describing its work correctly.
 */
export const pipelineStagesInputSchema = z
  .array(pipelineStageInputSchema)
  .min(1, 'A pipeline needs at least one stage')
  .max(MAX_PIPELINE_STAGES)
  .refine((stages) => stages.some((stage) => stage.forecastType === 'open'), {
    message: 'A pipeline needs at least one open stage for deals to sit in',
  });

export const createPipelineSchema = z.object({
  name: pipelineNameSchema,
  stages: pipelineStagesInputSchema,
  /** Makes this the pipeline new deals default to, moving the flag off whoever held it. */
  isDefault: z.boolean().optional(),
});
export type CreatePipelineInput = z.input<typeof createPipelineSchema>;

export const updatePipelineSchema = z
  .object({
    name: pipelineNameSchema.optional(),
    stages: pipelineStagesInputSchema.optional(),
    isDefault: z.boolean().optional(),
    active: z.boolean().optional(),
  })
  .refine((patch) => Object.keys(patch).length > 0, { message: 'Nothing to update' });
export type UpdatePipelineInput = z.input<typeof updatePipelineSchema>;

/**
 * Why a pipeline could not be deleted.
 *
 * Deleting is narrow on purpose. A pipeline with deals in it is the ladder those deals were sold
 * down — removing it would leave every one of them pointing at a stage nothing can name, which is
 * worse than a tidy settings screen is good. `active: false` is the answer for a ladder a shop has
 * stopped using, and it keeps the labels resolving.
 */
export const PIPELINE_DELETE_BLOCKED_REASONS = ['has_deals', 'last_pipeline'] as const;
export type PipelineDeleteBlockedReason = (typeof PIPELINE_DELETE_BLOCKED_REASONS)[number];

/**
 * What every shop starts with: New, Contacted, Quoted, Won, Lost — the words a tailoring shop
 * already uses — rather than Bigin's own default ladder (Qualification → Needs Analysis → Proposal
 * → Negotiation). This board answers "how far have we got with this piece of BUSINESS" and shows
 * only what somebody decided was worth selling.
 *
 * EVERY WORD OF IT IS EDITABLE, which is the point of the ladder being a document rather than an
 * enum. A shop that wants Bigin's sequence back, or its own — "Measured", "Cut", "Trial" — builds
 * it in Settings → Pipelines and this constant never applies to them again.
 *
 * The probabilities are a straight ramp, advisory, and exist so a forecast has something to weight. See
 * `pipelineStageSchema`.
 */
export const DEFAULT_PIPELINE_NAME = 'Sales Pipeline';

export const DEFAULT_PIPELINE_STAGES: readonly {
  key: string;
  label: string;
  forecastType: DealForecastType;
  probability: number;
}[] = [
  { key: 'new', label: 'New', forecastType: 'open', probability: 10 },
  { key: 'contacted', label: 'Contacted', forecastType: 'open', probability: 40 },
  { key: 'quoted', label: 'Quoted', forecastType: 'open', probability: 75 },
  /*
   * The two endings carry Bigin's own wording rather than a bare "Won" / "Lost", because those two
   * words are ambiguous on a board that also has an enquiry called "Won": "Closed with Sale" says
   * what actually happened to the piece of business.
   *
   * `forecastType` is what the product reads, and it is set on MEANING rather than copied from
   * Bigin: their org has BOTH of these marked `Closed Won`, which makes every abandoned deal count
   * as a sale in any figure grouped by forecast. "Closed without Sale" is a loss, and is recorded
   * as one here. The KEYS stay `won` / `lost` — short, stable, and never shown to anybody.
   */
  { key: 'won', label: 'Closed with Sale', forecastType: 'won', probability: 100 },
  { key: 'lost', label: 'Closed without Sale', forecastType: 'lost', probability: 0 },
];

/** The stages a deal can still be worked in, in board order. */
export function openStagesOf(pipeline: Pipeline): PipelineStage[] {
  return pipeline.stages.filter((stage) => stage.forecastType === 'open');
}

/**
 * Where a new deal starts: the first open stage on the ladder.
 *
 * Never the first stage outright, because a shop is free to put an ending anywhere — and a deal
 * created directly into "Closed Lost" is a deal nobody meant to create.
 */
export function entryStageOf(pipeline: Pipeline): PipelineStage | null {
  return openStagesOf(pipeline)[0] ?? null;
}

/** One stage by key, or null when it has been retired out from under a deal. */
export function stageOf(pipeline: Pipeline, key: string): PipelineStage | null {
  return pipeline.stages.find((stage) => stage.key === key) ?? null;
}
